import {
  Component,
  OnInit,
  ViewChild,
  InjectionToken,
  Inject,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProfileInfo } from 'app/features/register/models/profile-info.model';
import { RegistrationService } from 'app/features/register/services/registration.service';
import { ExperianProofingService } from 'app/features/register/services/experian-proofing.service';

import { UserIdentityVerifyService } from 'app/features/register/services/user-identity-verify.service';
import { Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { WorkflowService } from 'app/features/register/components/routeGuard/workflow.service';
import { QUOTE_STEPS } from 'app/features/register/components/routeGuard/workflow.model';

import { LocationService } from '@shared/services/location.service';
import { HarpLocation } from '@shared/services/location.service';
import { AlertService } from '@shared/services/alert.service';
import { RegexService, RegEx } from '@shared/services/regex.service';
import { SpinnerService } from '@shared/services/spinner.service';

import { SaviyntService } from 'app/features/register/services/savynt-unq-chk.service';
import { NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { UserProofingService } from 'app/features/register/services/user-proofing.service';

import * as PROFILE from './profile-info.constants';
import { RECAPTCHA_SETTINGS } from 'ng-recaptcha';
import { Observable } from 'rxjs';

const MODAL_ALERT_SERVICE = new InjectionToken<AlertService>('modal.alert');

const UNEXPECTED_ERROR_MSG =
  'An unexpected error has occurred. Please try again later.';
const EMAIL_ALREADY_EXISTS_ERROR_MSG =
  'An error occurred when creating your account. Your email is already in use. If you believe this is your account, please either contact Experian to complete proofing or contact the Services and Operations Support (SOS) team for assistance in completing registration. Otherwise, please create an account with a different email or contact the SOS team with error code: ERRREGS014.';
const INVALID_EXPERIAN_INFO_ERROR_MSG =
  'The information you provided to Experian does not match the information that you provided. Please review and correct your information.';
const EMAIL_ALREADY_EXISTS_ALT_ERROR_MSG =
  'Email already in use. Please log in with your existing credentials.';
const ACCOUNT_MAY_ALREADY_EXIST_ERROR_MSG =
  'An error occurred when creating your account. Your user account may already exist. If you previously attempted to create an account, please either contact Experian to complete proofing or contact the Services and Operations Support (SOS) team for assistance in completing the registration process. If this is not your account, please try again using "Manual Proofing" on the first page of registration.';
const USER_ALREADY_REGISTERED_WITH_HARP_ERROR_MSG =
  'Our records indicate that you have already registered with HARP. If you have forgotten your user ID, please select Forgot User ID.';
const UNABLE_TO_VALIDATE_INFO_ERROR_MSG =
  'The information provided could not be validated. Please check the information you entered and try again.';
const ACCOUNT_EXISTS_SO_YOU_CANT_RETRY_RIDP =
  'Your account seems to already exist, so Remote Identity Proofing cannot be retried. Please log in with your account credentials.';
const RIDP_RETRY_LIMIT_EXCEEDED = ({ referenceNum }) =>
  `<strong>Exceeded maximum Remote Identity Proofing attempts</strong><br>You have exceeded the maximum number of remote proofing attempts. Please contact Experian Verification Support Services at <a href="18332036550">1-833-203-6550</a> and provide your reference number: ${referenceNum}. If you need additional assistance completing registration, please contact  your application’s help desk.`;

@Component({
  selector: 'app-profile-info',
  templateUrl: './profile-info.component.html',
  styleUrls: ['./profile-info.component.scss'],
  providers: [{ provide: MODAL_ALERT_SERVICE, useClass: AlertService }],
})
export class ProfileInfoComponent implements OnInit {
  @ViewChild('cmsBypassProofingModal') cmsBypassProofingModal;
  @ViewChild('referenceNumber') farsModal;

  newProfileInfo: ProfileInfo = new ProfileInfo();

  isFarsFlowValue = false;
  isOktaOnlyUser = false;
  isRetryRIDPFlow = false;

  states: HarpLocation[];
  countries: HarpLocation[];

  openModalRef: NgbModalRef;

  // NOTE: Registration Form
  profileInfoForm: UntypedFormGroup;
  firstNameInputConfig = PROFILE.FIRST_NAME_INPUT_CONFIG;
  lastNameInputConfig = PROFILE.LAST_NAME_INPUT_CONFIG;
  middleNameInputConfig = PROFILE.MIDDLE_NAME_INPUT_CONFIG;
  dateOfBirthInputConfig = PROFILE.DATE_OF_BIRTH_INPUT_CONFIG;
  businessEmailAddressInputConfig = PROFILE.BUSINESS_EMAIL_ADDRESS_INPUT_CONFIG;
  confirmBusinessEmailAddressInputConfig =
    PROFILE.CONFIRM_BUSINESS_EMAIL_ADDRESS_INPUT_CONFIG;
  personalEmailAddressInputConfig = PROFILE.PERSONAL_EMAIL_ADDRESS_INPUT_CONFIG;
  confirmPersonalEmailAddressInputConfig =
    PROFILE.CONFIRM_PERSONAL_EMAIL_ADDRESS_INPUT_CONFIG;
  personalPhoneNumberInputConfig = PROFILE.PERSONAL_PHONE_NUMBER_INPUT_CONFIG;
  businessPhoneNumberInputConfig = PROFILE.BUSINESS_PHONE_NUMBER_INPUT_CONFIG;
  isUSAddressInputConfig = PROFILE.IS_US_ADDRESS_INPUT_CONFIG;
  addressLine1InputConfig = PROFILE.ADDRESS_LINE_1_INPUT_CONFIG;
  addressLine2InputConfig = PROFILE.ADDRESS_LINE_2_INPUT_CONFIG;
  cityInputConfig = PROFILE.CITY_INPUT_CONFIG;
  countryInputConfig = PROFILE.COUNTRY_INPUT_CONFIG;
  stateInputConfig = PROFILE.STATE_INPUT_CONFIG;
  zipCodeInputConfig = PROFILE.ZIP_CODE_INPUT_CONFIG;
  zipCodeExtensionInputConfig = PROFILE.ZIP_CODE_EXTENSION_INPUT_CONFIG;
  ssnInputConfig = PROFILE.SSN_INPUT_CONFIG;
  foreignAddressLine1InputConfig = PROFILE.FOREIGN_ADDRESS_LINE_1_INPUT_CONFIG;
  foreignAddressLine2InputConfig = PROFILE.FOREIGN_ADDRESS_LINE_2_INPUT_CONFIG;
  foreignPhoneNumberInputConfig = PROFILE.FOREIGN_PHONE_NUMBER_INPUT_CONFIG;

  // NOTE: Retry RIDP Form
  retryRIDPForm: UntypedFormGroup;
  retryUserIdInputConfig = PROFILE.RETRY_USER_ID_INPUT_CONFIG;
  retryPasswordInputConfig = PROFILE.RETRY_PASSWORD_INPUT_CONFIG;
  retryRIDPOptionsInputConfig = PROFILE.RETRY_RIDP_OPTIONS_INPUT_CONFIG;
  retrySSNInputConfig = PROFILE.RETRY_LAST_4_OF_SSN_INPUT_CONFIG;
  retryDobInputConfig = PROFILE.RETRY_DATE_OF_BIRTH_INPUT_CONFIG;
  retryReferenceIdInputConfig = PROFILE.RETRY_REFERENCE_NUMBER_INPUT_CONFIG;

  // NOTE: FARS Form
  farsForm: UntypedFormGroup;
  farsReferenceNumberInputConfig = PROFILE.FARS_REFERENCE_NUMBER_INPUT_CONFIG;
  farsEmailInputConfig = PROFILE.FARS_EMAIL_INPUT_CONFIG;

  hasProofError = false;
  captchaConfig$: Observable<any>;

  constructor(
    private modalService: NgbModal,
    public formBuilder: UntypedFormBuilder,
    private router: Router,
    private registrationService: RegistrationService,
    private userProofingService: UserProofingService,
    public experianProofingService: ExperianProofingService,
    public saviyntService: SaviyntService,
    public workflowService: WorkflowService,
    private locationService: LocationService,
    private spinnerService: SpinnerService,
    private userIdentityVerifyService: UserIdentityVerifyService,
    private fb: UntypedFormBuilder,
    @Inject(MODAL_ALERT_SERVICE) public modalAlertService,
    private regexService: RegexService,
    private calendar: NgbCalendar,
    @Inject(RECAPTCHA_SETTINGS) private captchaConfig: Observable<any>,
  ) {
    this.captchaConfig$ = this.captchaConfig;
  }

  ngOnInit() {
    this.countries = this.locationService.getCountries();
    this.states = this.locationService.getStates();

    this.initForms();
  }

  initiateManualProofFlow() {
    this.spinnerService.start('Loading...');
    this.registrationService
      .checkEmailUniqueness({ businessEmail: this.businessEmail })
      .subscribe({
        next: res => this.submitManualProofInfoSuccess(res),
        error: () => this.defaultErrorHandler(),
      });
  }

  initiateExperianProofingFlow() {
    if (this.isCMSUser) {
      this.openModal(this.cmsBypassProofingModal);
      return;
    }

    this.newProfileInfo.manualProofing = false;

    if (this.isRetryRIDPFlow) {
      this.submitRetryRIDP();
    } else if (this.isFarsFlow) {
      this.verifyFARSFlow();
    } else {
      // first attempt
      this.checkUniqueness();
    }
  }

  initiateRetryRIDP() {
    const {
      retryUserId,
      retryPassword,
      retryReferenceId,
      retrySSN,
      retryRIDPOptions,
      retryDob,
    } = this.retryRIDPForm.value;

    if (retryRIDPOptions) {
      this.attemptRIDPWithSsn({
        username: retryUserId,
        password: retryPassword,
        retryDob: this.browserToHarpDate(retryDob),
        retrySSN,
      });
    } else {
      this.attemptRIDPWithReferenceId({
        username: retryUserId,
        password: retryPassword,
        referenceId: retryReferenceId,
      });
    }
  }

  initiateFARSFlow(FARSInformationForm) {
    this.spinnerService.start('Loading...');
    this.clearError();
    const { referenceId, farsEmail: email } = this.farsForm.value;
    this.isFarsFlow = true;

    this.resetProfileInfo();

    this.userIdentityVerifyService.getUser(email, referenceId).subscribe({
      next: res => this.initiateFARSFlowSuccess(res),
      error: ({ error }) => {
        this.isFarsFlow = false;
        this.defaultErrorHandler({
          error: error?.errorSummary || UNABLE_TO_VALIDATE_INFO_ERROR_MSG,
          isModalError: true,
        });
      },
    });
  }

  openFarsModal() {
    this.farsForm.reset();
    this.openModal(this.farsModal, 'lg');
  }

  private submitManualProofInfoSuccess(res) {
    const { statusCode, status } = res;

    if (status) {
      this.defaultErrorHandler({
        error:
          statusCode === 'EMAIL_EXIST_S'
            ? EMAIL_ALREADY_EXISTS_ERROR_MSG
            : EMAIL_ALREADY_EXISTS_ALT_ERROR_MSG,
      });
      return;
    }

    if (!this.isUSAddress) {
      this.newProfileInfo.addressLine1 = this.foreignAddressLine1;
      this.newProfileInfo.addressLine2 = this.foreignAddressLine2;
    }
    this.newProfileInfo.manualProofing = true;

    this.setProfileInfo();

    this.workflowService.validateStep(QUOTE_STEPS.registrationStartPage);

    this.router.navigateByUrl('/register/account-info');
  }

  private attemptRIDPWithReferenceId({ username, password, referenceId }) {
    const payload = { username, password, referenceId };
    this.spinnerService.start('Loading...');
    this.registrationService
      .attemptRIDP(username, password, referenceId)
      .subscribe({
        next: res => this.attemptRetryRIDPSuccess(res, payload),
        error: ({ error }) => {
          this.defaultErrorHandler({
            error: error?.errorSummary || UNABLE_TO_VALIDATE_INFO_ERROR_MSG,
            isModalError: true,
          });
        },
      });
  }

  private attemptRIDPWithSsn({ username, password, retryDob, retrySSN }) {
    const payload = {
      username,
      password,
      retryDob,
      retrySSN,
    };

    this.spinnerService.start('Loading...');
    this.registrationService
      .attemptRIDPWithSsn(username, password, retryDob, retrySSN)
      .subscribe({
        next: res => this.attemptRetryRIDPSuccess(res, payload),
        error: ({ error }) => {
          this.defaultErrorHandler({
            error: error?.errorSummary || UNABLE_TO_VALIDATE_INFO_ERROR_MSG,
            isModalError: true,
          });
        },
      });
  }

  private attemptRetryRIDPSuccess(resp, payload) {
    const reproofErrorCode = resp.code;
    this.spinnerService.stop();
    this.openModalRef.close();

    if (!reproofErrorCode || reproofErrorCode === 'HARP-RRS-2001') {
      this.isRetryRIDPFlow = true;

      const {
        experianReferenceId,
        username,
        reproofAttempts,
        customproperty38,
        customproperty39,
        customproperty64,
      } = resp.userDetail;

      this.updateForm(resp.userDetail);

      this.newProfileInfo = {
        ...this.newProfileInfo,
        experianReferenceId,
        username,
        reproofingFlow: true,
        customproperty41: reproofAttempts || 0,
        customproperty38,
        customproperty39,
        customproperty64,
        loa3Flow: null,
        accountid: null,
        sourceType: null,
        loaStatus: null,
        pwd: null,
      };

      this.personalEmailControl.disable();
      this.confirmPersonalEmailControl.disable();

      this.businessEmailControl.disable();
      this.confirmBusinessEmailControl.disable();

      this.clearError();
      return;
    }

    this.isRetryRIDPFlow = false;

    switch (reproofErrorCode) {
      case 'HARP-RRS-2005':
        const { username, password, retryDob, retrySSN } = payload;

        return this.tryElevateUserToLOA3(
          username,
          password,
          retryDob,
          retrySSN,
        );
        break;
      case 'HARP-RRS-2004':
        const loginRefUrl = `<a target="_self" href=${window.location.origin}/login/login>Log into HARP</a>`;
        this.experianProofingService.errorMessage =
          ACCOUNT_EXISTS_SO_YOU_CANT_RETRY_RIDP + loginRefUrl;
        break;
      case 'HARP-RRS-2003':
        this.spinnerService.stop();
        this.experianProofingService.errorMessage =
          UNABLE_TO_VALIDATE_INFO_ERROR_MSG;
        break;
      case 'HARP-RRS-2002':
        const { referenceId } = payload;
        this.experianProofingService.errorMessage = RIDP_RETRY_LIMIT_EXCEEDED({
          referenceNum: referenceId || resp.referenceId,
        });
        break;
      default:
        this.experianProofingService.errorMessage = UNEXPECTED_ERROR_MSG;
        break;
    }
  }

  private updateForm(user) {
    this.profileInfoForm.patchValue({
      profile: {
        firstName: user.firstname,
        lastName: user.lastname,
        middleName: user.middleInitial,
        personalEmail: user.personalEmail,
        confirmPersonalEmail: user.personalEmail,
        businessEmail: user.businessEmail,
        confirmBusinessEmail: user.businessEmail,
        personalPhoneNumber: user.personalPhoneNumber,
        businessPhoneNumber: user.businessPhoneNumber,
        isUSAddress: ['USA', 'US'].includes(user.country) ? 1 : 0,
        addressLine1: user.addressLine1 || user.street,
        addressLine2: user.addressLine2,
        city: user.city,
        state: user.state,
        country: user.country,
        zipCode: user.zipCode || user.customproperty5,
        zipCodeExt: user.zipCodeExtension || user.customproperty6,
        dob: this.harpToBrowserDate(user.dateOfBirth || user.customproperty7),
      },
    });

    this.profileInfoForm.markAllAsTouched();
  }
  private tryElevateUserToLOA3(username, password, retryDob, retrySSN) {
    this.spinnerService.start('Loading...');
    this.userProofingService
      .getUserProfileFromOkta(username, password, retryDob, retrySSN)
      .subscribe({
        next: resp => this.tryElevateUserToLOA3Success(resp, username),
        error: error => this.tryElevateUserToLOA3Fail(error),
      });
  }

  private tryElevateUserToLOA3Fail(error) {
    this.resetProfileInfo();
    this.isRetryRIDPFlow = false;
    this.isOktaOnlyUser = false;
    this.defaultErrorHandler();
  }

  private tryElevateUserToLOA3Success(resp, username) {
    this.spinnerService.stop();

    const { errorCode } = resp;

    if (errorCode && errorCode !== '0') {
      this.isRetryRIDPFlow = false;
      this.isOktaOnlyUser = false;

      switch (errorCode) {
        case 'ERRPRFUPAPI5':
          this.experianProofingService.errorMessage = resp.errorMessage;
          break;
        case 'ERRPRFUPAPI1':
          this.experianProofingService.errorMessage = UNEXPECTED_ERROR_MSG;
          break;
        case 'ERRPRFUPAPI10':
          this.experianProofingService.errorMessage =
            "User is in an incorrect state. Please contact your application's help desk with error code: ERRPRFUPAPI10.";
          break;
        case 'ERRPRFUPAPI2':
          this.experianProofingService.errorMessage =
            'Insufficient user information provided.';
          break;
        case 'ERRPRFUPAPI7':
          const originUrl = window.location.origin;
          const loginRefUrl = `<a target="_self" href="${originUrl}/login/login">Log into HARP</a>`;
          this.experianProofingService.errorMessage = `This account already exists. Please log in with your existing credentials. ${loginRefUrl}`;
          break;
        default:
          this.experianProofingService.errorMessage = UNEXPECTED_ERROR_MSG;
          break;
      }

      return;
    }

    this.clearError();
    this.isRetryRIDPFlow = true;
    this.isOktaOnlyUser = true;

    this.newProfileInfo = {
      ...this.newProfileInfo,
      accountid: resp.accountid,
      sourceType: resp.sourceType,
      loaStatus: resp.loaStatus,
      socialSecurityNumber: undefined,
      firstname: resp.firstname,
      lastname: resp.lastname,
      middlename: resp.middlename,
      personalPhoneNumber: resp.primaryphonenumber,
      personalEmail: resp.personalEmail,
      confirmPersonalEmailAddress: resp.personalEmail,
      businessEmail: resp.businessEmail,
      confirmBusinessEmailAddress: resp.businessEmail,
      addressLine1: resp.street,
      city: resp.city,
      zipCode: resp.customproperty5,
      loa3Flow: resp.userorigintype,
      username,
      country: resp.country,
      state: resp.state,
      pwd: resp.pwd,
      dateOfBirth: resp.customproperty7,
    } as ProfileInfo;

    this.updateForm(this.newProfileInfo);
  }

  private initiateFARSFlowSuccess(resp) {
    this.isFarsFlow = true;

    this.spinnerService.stop();

    if (!resp) {
      return;
    }

    if (resp.userdetails?.length > 1) {
      this.isFarsFlow = false;
      this.defaultErrorHandler({
        error: 'We are unable to fetch your record.',
        isModalError: true,
      });
      return;
    }

    if (resp.msg !== 'Successful' || !resp.userdetails?.length) {
      this.defaultErrorHandler({
        error: UNABLE_TO_VALIDATE_INFO_ERROR_MSG,
        isModalError: true,
      });
      this.isFarsFlow = false;
      return;
    }

    const userDetails = resp.userdetails[0] ?? {};
    const { username, customproperty1 } = userDetails;

    if (customproperty1 === '3') {
      this.modalAlertService.setAlert(
        USER_ALREADY_REGISTERED_WITH_HARP_ERROR_MSG,
      );
      this.isFarsFlow = false;
      return;
    }

    this.updateForm(userDetails);
    this.setProfileInfo({
      username,
    });

    this.personalEmailControl.disable();
    this.confirmPersonalEmailControl.disable();

    this.businessEmailControl.disable();
    this.confirmBusinessEmailControl.disable();

    this.openModalRef.close();
  }

  private submitRetryRIDP() {
    const { loa3Flow } = this.newProfileInfo;
    this.spinnerService.start('Loading...');

    this.setProfileInfo();

    this.clearError();

    this.workflowService.validateStep(QUOTE_STEPS.registrationStartPage);

    if (loa3Flow === 'EIDM LOA 1') {
      this.router.navigate(['/register/account-info']);
      return;
    }

    this.workflowService.validateStep(QUOTE_STEPS.accountInfoPage);

    const profileInfo = this.registrationService.getProfileInfo();

    this.experianProofingService
      .proofUser(profileInfo)
      .then(res => this.proofUserSuccess(res));
  }

  private proofUserSuccess({ responseStatus, errorMessage }: any) {
    switch (responseStatus) {
      case 'CREATE-USER-SUCCESS':
        this.workflowService.validateSteps([QUOTE_STEPS.accountInfoPage]);
        this.router.navigate(['/register/confirmation']);
        break;
      case 'CREATE-USER-FAIL':
        this.hasProofError = true;
        this.defaultErrorHandler({ error: errorMessage });
        break;
    }

    this.spinnerService.stop();
  }

  private async checkUniqueness() {
    this.spinnerService.start('Loading...');

    const saviyntIsUnique = await this.saviyntIsUnique();

    if (!saviyntIsUnique) {
      return;
    }

    const isEmailUnique = await this.isEmailUnique();

    if (!isEmailUnique) {
      return;
    }

    this.workflowService.validateStep(QUOTE_STEPS.registrationStartPage);

    this.setProfileInfo();

    this.router.navigateByUrl('/register/account-info');
  }

  private setProfileInfo(additionalInfo?) {
    const {
      firstName,
      lastName,
      middleName,
      dob,
      personalEmail,
      confirmPersonalEmail,
      businessEmail,
      confirmBusinessEmail,
      personalPhoneNumber,
      businessPhoneNumber,
      isUSAddress,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      zipCode,
      zipCodeExt,
      ssn,
      foreignAddressLine1,
      foreignAddressLine2,
    } = this.profileInfo;

    this.newProfileInfo = {
      ...this.newProfileInfo,
      ...additionalInfo,
      firstname: firstName,
      lastname: lastName,
      middleInitial: middleName,
      dateOfBirth: this.browserToHarpDate(dob),
      personalEmail,
      confirmPersonalEmailAddress: confirmPersonalEmail,
      businessEmail,
      confirmEmailAddress: confirmBusinessEmail,
      personalPhoneNumber,
      businessPhoneNumber,
      addressInUSModel: isUSAddress,
      addressLine1: isUSAddress ? addressLine1 : foreignAddressLine1,
      addressLine2: isUSAddress ? addressLine2 : foreignAddressLine2,
      city,
      state,
      ...(country && { country }),
      zipCode,
      zipCodeExtension: zipCodeExt,
      socialSecurityNumber: ssn,
    };

    this.registrationService.setProfileInfo(this.newProfileInfo);
  }

  private async saviyntIsUnique() {
    const { firstName, lastName, dob, zipCode } = this.profileInfo;

    const { statusCode } = await this.saviyntService
      .saviyntUniquenessCheck({
        firstName,
        lastName,
        dob: this.browserToHarpDate(dob),
        zipCode,
      })
      .toPromise();

    if (statusCode !== 'User details not exist') {
      this.defaultErrorHandler({ error: ACCOUNT_MAY_ALREADY_EXIST_ERROR_MSG });
      return false;
    }

    return true;
  }

  private async isEmailUnique() {
    const { businessEmail } = this.profileInfo;

    const { status, statusCode, errorCode, errorCause } =
      (await this.registrationService
        .checkEmailUniqueness({
          businessEmail,
        })
        .toPromise()) as any;

    if (errorCode) {
      this.defaultErrorHandler({ error: errorCause });
      return false;
    }

    if (statusCode === 'EMAIL_EXIST_S') {
      this.defaultErrorHandler({ error: EMAIL_ALREADY_EXISTS_ALT_ERROR_MSG });
      return false;
    }

    if (status && statusCode !== 'EMAIL_EXIST_S') {
      this.defaultErrorHandler({ error: EMAIL_ALREADY_EXISTS_ALT_ERROR_MSG });
      return false;
    }

    return true;
  }

  verifyFARSFlow() {
    this.spinnerService.start('Loading...');

    const { referenceId, farsEmail: personalEmail } = this.farsForm.value;

    this.userIdentityVerifyService
      .verifyFARSFlow({ referenceId, email: personalEmail })
      .subscribe({
        next: response => this.verifyFARSSuccess(response),
        error: err => {
          this.hasProofError = true;
          this.defaultErrorHandler({
            error: err?.error?.errorSummary || 'Cannot verify FARS user.',
          });
        },
      });
  }

  private verifyFARSSuccess(response) {
    const { farsUserRequestInfo } = response;
    if (
      farsUserRequestInfo?.farsOverallResponse?.decision !==
      PROFILE.FARS_STATUS.CONTINUE
    ) {
      this.defaultErrorHandler({ error: INVALID_EXPERIAN_INFO_ERROR_MSG });
      return;
    }

    this.newProfileInfo = {
      ...this.newProfileInfo,
      customproperty1: '3',
      customproperty36: PROFILE.FARS_STATUS.CONTINUE,
      customproperty39: 'PROVISIONED_HARP_REGISTRATION',
      customproperty16: 'FARSSUCCESS',
      socialSecurityNumber: this.profileInfo.ssn,
      experianReferenceId: this.farsForm.value.referenceId,
      captchaToken: this.captchaControl.value,
    };

    this.setProfileInfo();

    this.updateUser({
      updatedUserProfileInfo: this.registrationService.getProfileInfo(),
    });
  }

  private updateUser(payload) {
    this.registrationService.updateUser(payload).subscribe({
      next: res => this.updateUserSuccess(res),
      error: error => this.updateUserFail(error),
    });
  }

  private updateUserSuccess(res) {
    if (res.errorCode) {
      this.defaultErrorHandler({ error: res.errorSummary });
      return;
    }

    this.workflowService.validateSteps([
      QUOTE_STEPS.registrationStartPage,
      QUOTE_STEPS.accountInfoPage,
    ]);

    this.router.navigate(['/register/confirmation']);
  }

  private updateUserFail(error) {
    if (['ERR-REG-004', '400'].includes(error.status)) {
      this.defaultErrorHandler({
        error: error.errorSummary || INVALID_EXPERIAN_INFO_ERROR_MSG,
      });
    } else {
      this.defaultErrorHandler();
    }
  }

  private defaultErrorHandler({
    error,
    isModalError,
  }: {
    error?: string;
    isModalError?: boolean;
  } = {}) {
    this.spinnerService.stop();

    const err = error || UNEXPECTED_ERROR_MSG;

    if (isModalError) {
      this.modalAlertService.setAlert(err);
    } else {
      this.experianProofingService.errorMessage = err;
    }
  }

  resetProfileInfo() {
    const { experianReferenceId } = this.newProfileInfo;
    this.newProfileInfo = new ProfileInfo();
    this.newProfileInfo.experianReferenceId = experianReferenceId;
    this.resetProfileInfoForm();
  }

  resolved(captchaResponse: string) {
    this.newProfileInfo.captchaToken = captchaResponse;
  }

  resetProfileInfoForm() {
    this.retryRIDPForm.reset({ retryRIDPOptions: true });
  }

  openModal(modal, size = 'md') {
    this.spinnerService.stop();
    this.clearError();
    this.openModalRef = this.modalService.open(modal, {
      backdrop: 'static',
      size,
    });
  }

  canManualProof() {
    for (const key in this.profileInfoControl.controls) {
      if (key === 'ssn') {
        continue;
      }

      const control = this.profileInfoControl.get(key);

      if (control.dirty && control.invalid) {
        return false;
      }
    }
    // this.clearError();
    return true;
  }

  private clearError() {
    this.experianProofingService.errorMessage = '';
  }

  private browserToHarpDate(value: string) {
    const [year, month, day] = value.split('-');
    return `${month}/${day}/${year}`;
  }

  private harpToBrowserDate(value: string) {
    const [month, day, year] = value.includes('-')
      ? value.trim().split('-')
      : value.trim().replace(/\/\//g, '/').split('/');

    return `${year}-${month}-${day}`;
  }

  private initForms() {
    this.initProfileInfoForm();
    this.initRetryRIPDForm();
    this.initFARSForm();
  }

  private initFARSForm() {
    this.farsForm = this.fb.group({
      referenceId: [, PROFILE.FARS_REFERENCE_NUMBER_VALIDATORS],
      farsEmail: [, PROFILE.FARS_EMAIL_VALIDATORS],
    });

    this.allFARSInputConfigs.forEach(config => {
      config.form = this.farsForm;
    });
  }

  private initRetryRIPDForm() {
    const userIdRegex = this.regexService.get(RegEx.USER_ID_REGEX);
    const passwordRegex = this.regexService.get(RegEx.PASSWORD_REGEX);

    this.retryRIDPForm = this.fb.group({
      retryUserId: [, PROFILE.RETRY_USER_ID_VALIDATORS(userIdRegex)],
      retryPassword: [, PROFILE.RETRY_PASSWORD_VALIDATORS(passwordRegex)],
      retryRIDPOptions: [true, PROFILE.RETRY_RIDP_OPTIONS_INPUT_CONFIG],
      retrySSN: [, PROFILE.RETRY_LAST_4_OF_SSN_VALIDATORS],
      retryDob: [, PROFILE.RETRY_DATE_OF_BIRTH_VALIDATORS],
      retryReferenceId: [],
    });

    this.allRetryRIDPInputConfigs.forEach(config => {
      config.form = this.retryRIDPForm;
    });

    /**
     * NOTE: Switch between SSN/DOB and Reference ID
     * form options.
     */
    this.retryRIDPOptionsControl.valueChanges.subscribe({
      next: value => {
        this.retryReferenceIdControl.reset();
        this.retrySSNControl.reset();
        this.retryDobControl.reset();

        if (value) {
          this.retryReferenceIdControl.clearValidators();
          this.retrySSNControl.setValidators(
            PROFILE.RETRY_LAST_4_OF_SSN_VALIDATORS,
          );
          this.retryDobControl.setValue(PROFILE.RETRY_DATE_OF_BIRTH_VALIDATORS);
        } else {
          this.retryReferenceIdControl.setValidators(
            PROFILE.RETRY_REFERENCE_NUMBER_VALIDATORS,
          );
          this.retrySSNControl.clearValidators();
          this.retryDobControl.clearValidators();
        }
      },
    });
  }

  private initProfileInfoForm() {
    const today = this.calendar.getToday();

    this.profileInfoForm = this.fb.group({
      profile: this.fb.group({
        firstName: ['', PROFILE.FIRST_NAME_VALIDATORS],
        lastName: ['', PROFILE.LAST_NAME_VALIDATORS],
        middleName: ['', PROFILE.MIDDLE_NAME_VALIDATORS],
        dob: ['', PROFILE.DATE_OF_BIRTH_VALIDATORS(today)],
        businessEmail: ['', PROFILE.BUSINESS_EMAIL_ADDRESS_VALIDATORS],
        confirmBusinessEmail: [
          '',
          PROFILE.CONFIRM_BUSINESS_EMAIL_ADDRESS_VALIDATORS,
        ],
        personalEmail: ['', PROFILE.PERSONAL_EMAIL_ADDRESS_VALIDATORS],
        confirmPersonalEmail: [
          '',
          PROFILE.CONFIRM_PERSONAL_EMAIL_ADDRESS_VALIDATORS,
        ],
        personalPhoneNumber: ['', PROFILE.PERSONAL_PHONE_NUMBER_VALIDATORS],
        businessPhoneNumber: ['', PROFILE.BUSINESS_PHONE_NUMBER_VALIDATORS],
        isUSAddress: [1, []],
        addressLine1: ['', PROFILE.ADDRESS_LINE_1_VALIDATORS],
        addressLine2: ['', PROFILE.ADDRESS_LINE_2_VALIDATORS],
        city: ['', PROFILE.CITY_IN_US_VALIDATORS],
        state: ['', PROFILE.STATE_VALIDATORS],
        country: [''],
        zipCode: ['', PROFILE.ZIP_CODE_VALIDATORS],
        zipCodeExt: ['', PROFILE.ZIP_CODE_EXTENSION_VALIDATORS],
        ssn: ['', PROFILE.SSN_VALIDATORS],
        foreignAddressLine1: [''],
        foreignAddressLine2: [''],
      }),
      captcha: [''],
    });

    this.allProfileInfoInputConfigs.forEach(config => {
      config.form = this.profileInfoForm.get('profile') as UntypedFormGroup;
    });

    this.stateInputConfig.options = this.states.map(({ name, abbrev }) => ({
      label: name,
      value: abbrev,
    }));

    this.countryInputConfig.options = this.countries.map(
      ({ name, abbrev }) => ({
        label: name,
        value: abbrev,
      }),
    );

    this.profileInfoControl.get('isUSAddress').valueChanges.subscribe({
      next: value => {
        if (value === 1) {
          this.cityControl.setValidators(PROFILE.CITY_IN_US_VALIDATORS);
          this.cityInputConfig.required = true;

          this.personalPhoneNumberInputConfig =
            PROFILE.PERSONAL_PHONE_NUMBER_INPUT_CONFIG;
          this.personalPhoneNumberControl.setValidators(
            PROFILE.PERSONAL_PHONE_NUMBER_VALIDATORS,
          );

          this.businessPhoneNumberInputConfig =
            PROFILE.BUSINESS_PHONE_NUMBER_INPUT_CONFIG;
          this.businessPhoneNumberControl.setValidators(
            PROFILE.BUSINESS_PHONE_NUMBER_VALIDATORS,
          );

          this.addressLine1Control.setValidators(
            PROFILE.ADDRESS_LINE_1_VALIDATORS,
          );
          this.foreignAddressLine1Control.clearValidators();
          this.foreignAddressLine2Control.clearValidators();
          this.zipCodeControl.setValidators(PROFILE.ZIP_CODE_VALIDATORS);
          this.stateControl.setValidators(PROFILE.STATE_VALIDATORS);
        } else {
          this.cityControl.setValidators(PROFILE.CITY_NOT_IN_US_VALIDATORS);

          this.personalPhoneNumberInputConfig =
            PROFILE.FOREIGN_PHONE_NUMBER_INPUT_CONFIG;
          this.personalPhoneNumberControl.setValidators(
            PROFILE.FOREIGN_PHONE_NUMBER_VALIDATORS,
          );

          this.foreignAddressLine1Control.setValidators(
            PROFILE.FOREIGN_ADDRESS_LINE_1_VALIDATORS,
          );
          this.foreignAddressLine2Control.setValidators(
            PROFILE.FOREIGN_ADDRESS_LINE_2_VALIDATORS,
          );
          this.addressLine1Control.clearValidators();
          this.zipCodeControl.clearValidators();
          this.stateControl.clearValidators();
        }

        PROFILE.COUNTRY_CHANGE_FIELDS_TO_RESET.forEach(field => {
          this.profileInfoControl.get(field).reset();
        });
      },
    });

    this.businessEmailControl.valueChanges.subscribe({
      next: email => {
        const confirmBusinessEmailControl = this.profileInfoControl.get(
          'confirmBusinessEmail',
        );
        confirmBusinessEmailControl.setValidators(
          PROFILE.CONFIRM_BUSINESS_EMAIL_ADDRESS_VALIDATORS(email),
        );

        confirmBusinessEmailControl.updateValueAndValidity();
      },
    });

    this.personalEmailControl.valueChanges.subscribe({
      next: email => {
        const confirmPersonalEmailControl = this.profileInfoControl.get(
          'confirmPersonalEmail',
        );
        confirmPersonalEmailControl.setValidators(
          PROFILE.CONFIRM_PERSONAL_EMAIL_ADDRESS_VALIDATORS(email),
        );

        confirmPersonalEmailControl.updateValueAndValidity();
      },
    });
  }

  private updateRecaptcha({ required }) {
    if (required) {
      this.captchaControl.setValidators(PROFILE.RECAPTCHA_VALIDATORS);
    } else {
      this.captchaControl.clearValidators();
    }
    this.captchaControl.updateValueAndValidity();
  }

  private get captchaControl() {
    return this.profileInfoForm.get('captcha');
  }

  private get profileInfo() {
    return this.profileInfoControl.getRawValue();
  }

  get profileInfoControl() {
    return this.profileInfoForm.get('profile') as UntypedFormGroup;
  }

  private get dob() {
    return this.profileInfo.dob;
  }

  private get businessEmailControl() {
    return this.profileInfoControl.get('businessEmail');
  }

  private get confirmBusinessEmailControl() {
    return this.profileInfoControl.get('confirmBusinessEmail');
  }

  private get personalEmailControl() {
    return this.profileInfoControl.get('personalEmail');
  }

  private get confirmPersonalEmailControl() {
    return this.profileInfoControl.get('confirmPersonalEmail');
  }

  private get cityControl() {
    return this.profileInfoControl.get('city');
  }

  private get zipCodeControl() {
    return this.profileInfoControl.get('zipCode');
  }

  private get foreignAddressLine2Control() {
    return this.profileInfoControl.get('foreignAddressLine2');
  }

  private get addressLine1Control() {
    return this.profileInfoControl.get('addressLine1');
  }

  private get foreignAddressLine1Control() {
    return this.profileInfoControl.get('foreignAddressLine1');
  }

  private get retryRIDPOptionsControl() {
    return this.retryRIDPForm.get('retryRIDPOptions');
  }

  private get retrySSNControl() {
    return this.retryRIDPForm.get('retrySSN');
  }

  private get retryDobControl() {
    return this.retryRIDPForm.get('retryDob');
  }

  private get stateControl() {
    return this.profileInfoControl.get('state');
  }

  private get retryReferenceIdControl() {
    return this.retryRIDPForm.get('retryReferenceId');
  }

  private get iKnowLast4OfSSN() {
    return this.retryRIDPForm.value.retryRIDPOptions;
  }

  private get personalEmail() {
    return this.profileInfo.personalEmail;
  }

  private get businessEmail() {
    return this.profileInfo.businessEmail;
  }

  private get foreignAddressLine1() {
    return this.profileInfo.foreignAddressLine1;
  }

  private get foreignAddressLine2() {
    return this.profileInfo.foreignAddressLine2;
  }

  private get isCMSUser() {
    return this.businessEmail.split('@').pop() === 'cms.hhs.gov';
  }

  get hideInitiateManualProofing() {
    return this.isFarsFlow || this.newProfileInfo?.reproofingFlow;
  }

  private get allFARSInputConfigs() {
    return [this.farsReferenceNumberInputConfig, this.farsEmailInputConfig];
  }

  get isUSAddress() {
    return !!this.profileInfo?.isUSAddress;
  }

  private get personalPhoneNumberControl() {
    return this.profileInfoControl.get('personalPhoneNumber');
  }

  private get businessPhoneNumberControl() {
    return this.profileInfoControl.get('businessPhoneNumber');
  }

  private get allProfileInfoInputConfigs() {
    return [
      this.firstNameInputConfig,
      this.lastNameInputConfig,
      this.middleNameInputConfig,
      this.dateOfBirthInputConfig,
      this.businessEmailAddressInputConfig,
      this.confirmBusinessEmailAddressInputConfig,
      this.personalEmailAddressInputConfig,
      this.confirmPersonalEmailAddressInputConfig,
      this.personalPhoneNumberInputConfig,
      this.businessPhoneNumberInputConfig,
      this.isUSAddressInputConfig,
      this.addressLine1InputConfig,
      this.addressLine2InputConfig,
      this.cityInputConfig,
      this.stateInputConfig,
      this.countryInputConfig,
      this.zipCodeInputConfig,
      this.zipCodeExtensionInputConfig,
      this.ssnInputConfig,
      this.foreignAddressLine1InputConfig,
      this.foreignAddressLine2InputConfig,
      this.foreignPhoneNumberInputConfig,
    ];
  }

  private get allRetryRIDPInputConfigs() {
    return [
      this.retryUserIdInputConfig,
      this.retryPasswordInputConfig,
      this.retryRIDPOptionsInputConfig,
      this.retrySSNInputConfig,
      this.retryDobInputConfig,
      this.retryReferenceIdInputConfig,
    ];
  }

  set isFarsFlow(value) {
    if (value) {
      this.updateRecaptcha({ required: true });
    } else {
      this.updateRecaptcha({ required: false });
    }
    this.isFarsFlowValue = value;
  }

  get isFarsFlow() {
    return this.isFarsFlowValue;
  }

  get canInitiateManualProofing() {
    const form = this.profileInfoForm.controls.profile as UntypedFormGroup;
    for (const name in form.controls) {
      if (name === 'ssn') {
        continue;
      }
      const control = form.get(name);

      if (control?.invalid) {
        return false;
      }
    }
    return true;
  }
}
